import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { RouterLink, RouterLinkActive, RouterOutlet, Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { Subject, takeUntil } from 'rxjs';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { AppService } from '../../service/app.service';
import { AuthService } from '../../service/auth.service';
import { DataServiceService } from '../../service/data-service.service';
import { UserData } from '../../interface/userData.interface';
import UserUtils from '../../utils/user.utils';
import { CustomFormValidator } from '../../formValidators/customForm.validator';
import { TermsOffcanvasComponent } from '../../component/terms-offcanvas/terms-offcanvas.component';
import { NotificationBellComponent } from '../../component/notification-bell/notification-bell.component';
import { VersionInfoComponent } from '../../component/version-info/version-info.component';
import { ChatWidgetComponent } from '../../component/chat-widget/chat-widget.component';
import { MessagingService } from '../../service/messaging.service';
import { SiteLeadsCounterService } from '../../service/site-leads-counter.service';
import { ToastrService } from 'ngx-toastr';

type SidebarSection = 'company' | 'operations' | 'administration' | 'reports';

const SIDEBAR_SECTIONS_KEY = 'sidebarSectionsOpen';

// Top-to-bottom order sections actually appear in the template — used to pick
// the "first" one to default open for a given user (sections are role-gated,
// so which one is literally first on screen depends on the user's role).
const SECTION_ORDER: SidebarSection[] = ['company', 'operations', 'administration', 'reports'];

@Component({
  selector: 'app-auth-layout',
  standalone: true,
  imports: [RouterOutlet, RouterLink, RouterLinkActive, TermsOffcanvasComponent, ReactiveFormsModule, NotificationBellComponent, VersionInfoComponent, ChatWidgetComponent],
  templateUrl: './auth-layout.component.html',
  styleUrl: './auth-layout.component.scss',
})
export class AuthLayoutComponent implements OnInit, OnDestroy {
  title = '';
  subtitle = '';
  user = this.authService.getUser() as UserData;
  showTermsModal = false;
  readonly currentYear = new Date().getFullYear();

  readonly adminLevel = UserUtils.SECURITY_LEVEL_ADMIN;
  readonly salesLevel = UserUtils.SECURITY_LEVEL_SALES;
  chatWidgetActive = false;
  readonly userrole_salesPerson = UserUtils.USERROLE_SALESPERSON;
  readonly userrole_employer = UserUtils.USERROLE_EMPLOYER;

  sidebarCollapsed = false;

  // SL-05: leads del sitio disponibles, en el menú del vendedor.
  availableLeads = 0;

  // Collapsible sidebar sections — only the first section visible to this user
  // (top to bottom) starts open, the rest start collapsed, to keep the menu
  // from overflowing the viewport. A user's own toggles always win over this
  // default — see restoreSectionsState().
  sectionsOpen: Record<SidebarSection, boolean> = this.defaultSectionsOpen();

  showChangePasswordModal = false;
  changePasswordLoading = false;
  changePasswordError = '';

  changePasswordForm = new FormGroup({
    currentPassword: new FormControl('', [Validators.required]),
    newPassword: new FormControl('', [Validators.required, Validators.minLength(8), CustomFormValidator.validatePassword('')]),
    confirmPassword: new FormControl('', [Validators.required])
  }, { validators: CustomFormValidator.passwordsMatch('newPassword', 'confirmPassword') });

  private destroy$ = new Subject<void>();

  constructor(
    private appService: AppService,
    private authService: AuthService,
    private dataService: DataServiceService,
    private messaging: MessagingService,
    private siteLeadsCounter: SiteLeadsCounterService,
    private toastr: ToastrService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private titleService: Title
  ) {}

  ngOnInit(): void {
    this.sidebarCollapsed = localStorage.getItem('sidebarCollapsed') === 'true';
    this.restoreSectionsState();
    this.appService.currentMessage
      .pipe(takeUntil(this.destroy$))
      .subscribe(message => {
        this.title = message.title;
        this.subtitle = message.subtitle;
        this.updateDocumentTitle();
        const user = this.authService.getUser();
        if (user) this.user = user;
        this.cdr.detectChanges();
      });
    this.messaging.start();
    this.siteLeadsCounter.start();
    this.siteLeadsCounter.availableCount$
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => { this.availableLeads = count; this.cdr.markForCheck(); });

    if (this.user.securityLevel >= this.salesLevel) {
      this.dataService.getChatStatus()
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => { this.chatWidgetActive = res.active; },
          error: () => { this.chatWidgetActive = false; },
        });
    }
  }

  // Browser tab mirrors the page header (title + subtitle). Without this the
  // tab keeps the login route's title for the whole session, because no
  // authenticated route declares its own `title`.
  private updateDocumentTitle(): void {
    const parts = [this.title, this.subtitle].map(p => (p ?? '').trim()).filter(Boolean);
    this.titleService.setTitle(parts.length ? parts.join(' · ') : 'AlliantFed Portal');
  }

  toggleSidebar(): void {
    this.sidebarCollapsed = !this.sidebarCollapsed;
    localStorage.setItem('sidebarCollapsed', String(this.sidebarCollapsed));
  }

  toggleSection(section: SidebarSection): void {
    this.sectionsOpen[section] = !this.sectionsOpen[section];
    localStorage.setItem(SIDEBAR_SECTIONS_KEY, JSON.stringify(this.sectionsOpen));
  }

  /** Icon-only sidebar hides section headers, so items must always show there. */
  isSectionOpen(section: SidebarSection): boolean {
    return this.sidebarCollapsed || this.sectionsOpen[section];
  }

  private restoreSectionsState(): void {
    try {
      const stored = JSON.parse(localStorage.getItem(SIDEBAR_SECTIONS_KEY) ?? '{}');
      for (const key of Object.keys(this.sectionsOpen) as SidebarSection[]) {
        if (typeof stored[key] === 'boolean') this.sectionsOpen[key] = stored[key];
      }
    } catch {
      // Corrupt value — keep the role-aware defaults computed in defaultSectionsOpen()
    }
  }

  // Only the first section this user can actually see (top to bottom, per
  // SECTION_ORDER) starts open; every other section starts collapsed. Sections
  // the user has never toggled keep this default — restoreSectionsState() only
  // overrides keys explicitly present in localStorage, so an explicit user
  // choice always wins over this.
  private defaultSectionsOpen(): Record<SidebarSection, boolean> {
    const isVisible: Record<SidebarSection, boolean> = {
      company:        this.user?.idUserRole === this.userrole_employer,
      operations:     this.user?.securityLevel === this.adminLevel,
      administration: this.user?.securityLevel === this.adminLevel,
      reports:        (this.user?.securityLevel ?? 0) >= this.adminLevel,
    };
    const firstVisible = SECTION_ORDER.find(section => isVisible[section]);

    return {
      company: false,
      operations: false,
      administration: false,
      reports: false,
      ...(firstVisible ? { [firstVisible]: true } : {}),
    };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.messaging.stop();
    this.siteLeadsCounter.stop();
  }

  logout(): void {
    this.authService.clearSession();
    this.router.navigate(['/login']);
  }

  openChangePasswordModal(): void {
    this.changePasswordForm.reset();
    this.changePasswordError = '';
    this.showChangePasswordModal = true;
  }

  closeChangePasswordModal(): void {
    this.showChangePasswordModal = false;
    this.changePasswordError = '';
  }

  submitChangePassword(): void {
    if (this.changePasswordForm.invalid) return;
    const { currentPassword, newPassword } = this.changePasswordForm.value;
    this.changePasswordLoading = true;
    this.changePasswordError = '';
    this.dataService.updateUserPassword(this.user.idUser, {
      currentPassword: currentPassword!,
      newPassword: newPassword!
    }).subscribe({
      next: () => {
        this.changePasswordLoading = false;
        this.toastr.success('Password updated successfully', 'Success');
        this.closeChangePasswordModal();
      },
      error: err => {
        this.changePasswordLoading = false;
        this.changePasswordError = err.error?.message ?? 'Error updating password. Please try again.';
      }
    });
  }
}
